import { Download } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

/** The accord as the API sends it (GET /v1/partner/agreement). */
export interface AgreementData {
  version: string;
  title: string;
  washedSignatory: string;
  articles: { title: string; body: string }[];
  providers: Record<'MIXX' | 'FLOOZ', string>;
  displayName: string;
  signed: SignedAgreement | null;
}
export interface SignedAgreement {
  version: string;
  signerName: string;
  signedAt: string;
  /** Null while an applicant waits: the campaign starts the day the office approves. */
  campaignStart: string | null;
  campaignEnd: string | null;
  sha256: string;
  text: string;
}
type T = (key: string) => string;
type Request = <R>(path: string, body?: object) => Promise<R>;
type Problem = 'agreementPhoneInvalid' | 'agreementNameRequired' | 'agreementChanged' | 'error';

/** The same check as the API (creator-agreement.ts): 8 digits starting 7 or 9, 228 optional. */
export const togoDigits = (value: string): string | null => {
  const digits = value.replace(/\D/gu, '');
  const local = digits.length === 11 && digits.startsWith('228') ? digits.slice(3) : digits;
  return /^[79]\d{7}$/u.test(local) ? local : null;
};
const fullName = (value: string) => value.trim().replace(/\s+/gu, ' ').split(' ').length >= 2;
const problemFor = (status: number, code: string): Problem =>
  code.endsWith('agreement_phone_invalid')
    ? 'agreementPhoneInvalid'
    : code.endsWith('agreement_name_required')
      ? 'agreementNameRequired'
      : status === 409 && code.endsWith('agreement_version_changed')
        ? 'agreementChanged'
        : 'error';

function Articles({ data }: { data: AgreementData }) {
  return (
    <ol className="agreement-articles">
      {data.articles.map((a) => (
        <li key={a.title}>
          <strong>{a.title}</strong> {a.body}
        </li>
      ))}
    </ol>
  );
}

/**
 * Replaces the paper « Accord créateur Washed » (founder request 2026-11-02): the creator reads
 * it, fills in what the paper asked for and signs by typing her name. Signing also activates her
 * code when she had not accepted the terms yet.
 */
export function AgreementSign({
  t,
  request,
  onSigned,
}: {
  t: T;
  request: Request;
  onSigned: (signed: SignedAgreement) => void;
}) {
  const [data, setData] = useState<AgreementData | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [accounts, setAccounts] = useState('');
  const [provider, setProvider] = useState<'MIXX' | 'FLOOZ' | null>(null);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const load = () => {
    setLoadFailed(false);
    request<AgreementData>('agreement')
      .then((d) => (d.signed ? onSigned(d.signed) : setData(d)))
      .catch(() => setLoadFailed(true));
  };
  useEffect(load, []);
  if (loadFailed)
    return (
      <div className="notice problem" role="alert">
        {t('error')}{' '}
        <button className="text-button" onClick={load}>
          {t('retry')}
        </button>
      </div>
    );
  if (!data)
    return (
      <p className="caption" role="status">
        {t('loading')}
      </p>
    );
  // Filled in and ticked is enough to tap: a bad number or a single name is explained on tap,
  // never left as a grey button with no reason.
  const ready = !!accounts.trim() && !!provider && !!number.trim() && !!name.trim() && consent;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!togoDigits(number)) return setProblem('agreementPhoneInvalid');
    if (!fullName(name)) return setProblem('agreementNameRequired');
    if (!ready || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await request<{ signed: SignedAgreement }>('agreement/sign', {
        version: data!.version,
        consent,
        signerName: name,
        accounts,
        provider,
        mobileMoneyNumber: number,
      });
      onSigned(result.signed);
    } catch (err) {
      // api() in App.tsx throws the HTTP status as the message and the API's code beside it.
      setProblem(problemFor(Number((err as Error).message), (err as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>{t('agreementTitle')}</h1>
      <p className="lead">{t('agreementIntro')}</p>
      <form className="agreement-form" onSubmit={(e) => void submit(e)} noValidate>
        <h2>{t('agreementDetails')}</h2>
        <div className="field">
          <label htmlFor="agreement-accounts">{t('agreementAccounts')}</label>
          <input
            id="agreement-accounts"
            value={accounts}
            maxLength={300}
            autoComplete="off"
            placeholder="TikTok @…"
            onChange={(e) => setAccounts(e.target.value)}
          />
        </div>
        <div className="field">
          <span className="label" id="agreement-provider">
            {t('agreementProvider')}
          </span>
          <div className="segmented" role="group" aria-labelledby="agreement-provider">
            {(['MIXX', 'FLOOZ'] as const).map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={provider === p}
                onClick={() => setProvider(p)}
              >
                {data.providers[p]}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="agreement-number">{t('agreementNumber')}</label>
          <div className="phone">
            <span aria-hidden="true">+228</span>
            <input
              id="agreement-number"
              value={number}
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="90 12 34 56"
              aria-describedby="agreement-number-hint"
              aria-invalid={problem === 'agreementPhoneInvalid'}
              onChange={(e) => {
                setNumber(e.target.value);
                if (problem === 'agreementPhoneInvalid') setProblem(null);
              }}
            />
          </div>
          <p className="caption" id="agreement-number-hint">
            {t('agreementNumberHint')}
          </p>
        </div>
        <section className="group agreement-text" lang="fr" aria-labelledby="agreement-title">
          <h2 id="agreement-title">{data.title}</h2>
          <p className="caption">{t('agreementFrench')}</p>
          <Articles data={data} />
        </section>
        <div className="field">
          <label htmlFor="agreement-name">{t('agreementName')}</label>
          <input
            id="agreement-name"
            className="signature"
            value={name}
            maxLength={120}
            autoComplete="name"
            aria-invalid={problem === 'agreementNameRequired'}
            onChange={(e) => {
              setName(e.target.value);
              if (problem === 'agreementNameRequired') setProblem(null);
            }}
          />
        </div>
        <label className="consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>{t('agreementConsent')}</span>
        </label>
        {problem && (
          <div className="notice problem" role="alert">
            {t(problem)}
            {problem === 'agreementChanged' && (
              <>
                {' '}
                <button type="button" className="text-button" onClick={() => location.reload()}>
                  {t('reload')}
                </button>
              </>
            )}
          </div>
        )}
        <button className="primary wide" disabled={!ready || busy}>
          {t('agreementSign')}
        </button>
        <p className="caption">
          {t('agreementWashedBy')} {data.washedSignatory}. {t('agreementCopyByEmail')}
        </p>
      </form>
    </>
  );
}

/** The creator's own signed copy, under « Mon lien ». */
export function AgreementCopy({
  t,
  signed,
  date,
}: {
  t: T;
  signed: SignedAgreement;
  date: (iso: string) => string;
}) {
  return (
    <section className="section terms agreement-copy">
      <h2>{t('agreementMine')}</h2>
      <p>
        {t('agreementSignedOn')} {date(signed.signedAt)} {t('agreementBy')} {signed.signerName}.{' '}
        {signed.campaignStart && signed.campaignEnd
          ? `${t('agreementCampaign')} : ${date(`${signed.campaignStart}T12:00:00Z`)} – ${date(`${signed.campaignEnd}T12:00:00Z`)}.`
          : t('agreementCampaignPending')}
      </p>
      {/* A same-origin GET: the session cookie goes with it and the API answers with the PDF. */}
      <a className="secondary agreement-download" href="/v1/partner/agreement/pdf" download>
        <Download size={18} aria-hidden="true" />
        {t('agreementDownload')}
      </a>
      <details>
        <summary className="text-button">{t('agreementRead')}</summary>
        <pre className="agreement-signed" lang="fr">
          {signed.text}
        </pre>
        <p className="caption agreement-hash">
          {t('agreementFingerprint')} · <span translate="no">{signed.sha256}</span>
        </p>
      </details>
    </section>
  );
}
